import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertTriangle, Upload, Plus, X, Search, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWCreate = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit/');

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [permitNumber, setPermitNumber] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    permit_type: '',
    work_description: '',
    location: '',
    work_start_date: '',
    work_end_date: '',
    applicant_name: '',
    contractor_company: '',
    hazards_identified: [],
    control_measures: [],
    safety_checklist: [],
    workers: [],
    gas_test_readings: [],
    isolation_points: [],
    emergency_contact_name: '',
    emergency_contact_number: '',
    rescue_plan_available: false,
    rescue_plan_description: '',
    method_statement_url: '',
    risk_assessment_url: ''
  });

  // Template data
  const [checklistTemplate, setChecklistTemplate] = useState([]);
  const [gasTestRequired, setGasTestRequired] = useState(false);
  const [isolationRequired, setIsolationRequired] = useState(false);
  const [rescuePlanRequired, setRescuePlanRequired] = useState(false);
  
  // Employee search
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [validatingCompetency, setValidatingCompetency] = useState(false);
  
  // HIRA integration
  const [hiraRecords, setHiraRecords] = useState([]);
  const [selectedHIRA, setSelectedHIRA] = useState(null);

  const permitTypes = [
    'Hot Work',
    'Confined Space',
    'Work at Height',
    'Electrical Work',
    'Excavation',
    'Lifting Operations',
    'Chemical Handling',
    'Isolation Permit',
    'Radiography',
    'General Maintenance'
  ];

  // Required certifications for each permit type
  const requiredCertifications = {
    'Hot Work': ['Hot Work Safety', 'Fire Prevention'],
    'Confined Space': ['Confined Space Entry', 'Gas Testing', 'Rescue Operations'],
    'Work at Height': ['Work at Height', 'Fall Protection'],
    'Electrical Work': ['Electrical Safety', 'LOTO'],
    'Excavation': ['Excavation Safety', 'Shoring'],
    'Lifting Operations': ['Lifting Operations', 'Rigging', 'Crane Operations'],
    'Chemical Handling': ['Chemical Safety', 'Hazmat Handling'],
    'Isolation Permit': ['LOTO', 'Energy Isolation'],
    'Radiography': ['Radiation Safety', 'Radiography Operations'],
    'General Maintenance': []
  };

  useEffect(() => {
    fetchEmployees();
    fetchHIRARecords();
    if (isEditMode && editId) {
      fetchPermitData();
    }
  }, []);

  // Fetch existing permit data for edit mode
  const fetchPermitData = async () => {
    try {
      setInitialLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permits/${editId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const permit = response.data;
      setPermitNumber(permit.permit_number || '');

      // Convert ISO dates to datetime-local format
      const formatDateForInput = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        permit_type: permit.permit_type || '',
        work_description: permit.work_description || '',
        location: permit.location || '',
        work_start_date: formatDateForInput(permit.work_start_date),
        work_end_date: formatDateForInput(permit.work_end_date),
        applicant_name: permit.applicant_name || '',
        contractor_company: permit.contractor_company || '',
        hazards_identified: permit.hazards_identified || [],
        control_measures: permit.control_measures || [],
        safety_checklist: permit.safety_checklist || [],
        workers: permit.workers || [],
        gas_test_readings: permit.gas_test_readings || [],
        isolation_points: permit.isolation_points || [],
        emergency_contact_name: permit.emergency_contact_name || '',
        emergency_contact_number: permit.emergency_contact_number || '',
        rescue_plan_available: permit.rescue_plan_available || false,
        rescue_plan_description: permit.rescue_plan_description || '',
        method_statement_url: permit.method_statement_url || '',
        risk_assessment_url: permit.risk_assessment_url || ''
      });

      // Set template requirements based on permit type
      setGasTestRequired(permit.gas_test_required || false);
      setIsolationRequired(permit.isolation_required || false);
      setRescuePlanRequired(permit.permit_type === 'Confined Space');

      setInitialLoading(false);
    } catch (err) {
      console.error('Failed to fetch permit:', err);
      setError('Failed to load permit data');
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (formData.permit_type) {
      fetchPermitTemplate();
    }
  }, [formData.permit_type]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchHIRARecords = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch approved risk assessments for linking
      const response = await axios.get(`${API}/risk-assessments?status=Approved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHiraRecords(response.data);
    } catch (err) {
      console.error('Failed to fetch HIRA records:', err);
    }
  };

  const fetchPermitTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permits/templates/${formData.permit_type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setChecklistTemplate(response.data.checklist);
      setGasTestRequired(response.data.gas_test_required || false);
      setIsolationRequired(response.data.isolation_required || false);
      setRescuePlanRequired(response.data.rescue_plan_required || false);
      
      // Initialize checklist in form data
      setFormData(prev => ({
        ...prev,
        safety_checklist: response.data.checklist.map(item => ({
          ...item,
          id: Math.random().toString(36).substr(2, 9)
        }))
      }));
    } catch (err) {
      console.error('Failed to fetch template:', err);
    }
  };

  const validateWorkerCompetency = async (employeeId) => {
    setValidatingCompetency(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/permits/validate-worker-competency`,
        {
          employee_id: employeeId,
          required_certifications: requiredCertifications[formData.permit_type] || []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setValidatingCompetency(false);
      return response.data;
    } catch (err) {
      setValidatingCompetency(false);
      console.error('Competency validation failed:', err);
      return null;
    }
  };

  const handleAddWorker = async () => {
    if (!selectedEmployee) return;
    
    const employeeId = selectedEmployee.id || selectedEmployee.user_id;
    const competencyData = await validateWorkerCompetency(employeeId);
    
    if (competencyData) {
      setFormData(prev => ({
        ...prev,
        workers: [...prev.workers, {
          id: Math.random().toString(36).substr(2, 9),
          employee_id: employeeId,
          employee_name: competencyData.employee_name || selectedEmployee.name,
          required_certifications: requiredCertifications[formData.permit_type] || [],
          has_valid_certifications: competencyData.has_valid_certifications,
          missing_certifications: competencyData.missing_certifications || []
        }]
      }));
      
      setSelectedEmployee(null);
      setEmployeeSearch('');
    }
  };

  const handleRemoveWorker = (workerId) => {
    setFormData(prev => ({
      ...prev,
      workers: prev.workers.filter(w => w.id !== workerId)
    }));
  };

  const handleAddGasReading = () => {
    setFormData(prev => ({
      ...prev,
      gas_test_readings: [...prev.gas_test_readings, {
        id: Math.random().toString(36).substr(2, 9),
        test_time: new Date().toISOString(),
        oxygen_level: '',
        lel_level: '',
        h2s_level: '',
        co_level: '',
        tester_name: '',
        remarks: ''
      }]
    }));
  };

  const handleUpdateGasReading = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      gas_test_readings: prev.gas_test_readings.map(reading =>
        reading.id === id ? { ...reading, [field]: value } : reading
      )
    }));
  };

  const handleAddIsolationPoint = () => {
    setFormData(prev => ({
      ...prev,
      isolation_points: [...prev.isolation_points, {
        id: Math.random().toString(36).substr(2, 9),
        equipment_tag: '',
        isolation_type: 'Electrical',
        lock_number: '',
        isolation_verified_by: '',
        remarks: ''
      }]
    }));
  };

  const handleUpdateIsolationPoint = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      isolation_points: prev.isolation_points.map(point =>
        point.id === id ? { ...point, [field]: value } : point
      )
    }));
  };

  const handleChecklistChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      safety_checklist: prev.safety_checklist.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const linkHIRA = (hiraId) => {
    const hira = hiraRecords.find(h => h.id === hiraId);
    if (hira) {
      setSelectedHIRA(hira);
      // Extract hazards and controls from Risk Assessment record
      const hazards = [];
      const controls = [];

      // Risk Assessment hazards are objects with hazard_description and control_measures
      if (hira.hazards && Array.isArray(hira.hazards)) {
        hira.hazards.forEach(hazard => {
          // Extract hazard description
          if (hazard.hazard_description) {
            hazards.push(hazard.hazard_description);
          }
          // Extract control measures from each hazard
          if (hazard.control_measures && Array.isArray(hazard.control_measures)) {
            hazard.control_measures.forEach(cm => {
              if (cm.description) {
                controls.push(`[${cm.control_type || 'Control'}] ${cm.description}`);
              }
            });
          }
        });
      }

      setFormData(prev => ({
        ...prev,
        hazards_identified: hazards.length > 0 ? hazards : prev.hazards_identified,
        control_measures: controls.length > 0 ? controls : prev.control_measures
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));

      console.log('User from localStorage:', user);

      // Ensure applicant_id is available (user.id from login response)
      const userId = user.id || user.user_id;
      if (!user || !userId) {
        setError('User session invalid. Please log in again.');
        setLoading(false);
        return;
      }

      // Prepare payload - match backend PermitToWork model
      const payload = {
        permit_type: formData.permit_type,
        work_description: formData.work_description,
        location: formData.location,
        applicant_id: userId,
        applicant_name: formData.applicant_name || user.name || 'Unknown',
        contractor_company: formData.contractor_company || '',
        work_start_date: new Date(formData.work_start_date).toISOString(),
        work_end_date: new Date(formData.work_end_date).toISOString(),
        hazards_identified: formData.hazards_identified || [],
        control_measures: formData.control_measures || [],
        safety_checklist: formData.safety_checklist || [],
        workers: formData.workers || [],
        gas_test_readings: (formData.gas_test_readings || []).map(reading => ({
          ...reading,
          test_time: new Date(reading.test_time).toISOString()
        })),
        gas_test_required: gasTestRequired,
        isolation_points: formData.isolation_points || [],
        isolation_required: isolationRequired,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_number: formData.emergency_contact_number,
        rescue_plan_available: formData.rescue_plan_available || false,
        rescue_plan_description: formData.rescue_plan_description || '',
        method_statement_url: formData.method_statement_url || '',
        risk_assessment_url: formData.risk_assessment_url || '',
        all_workers_competent: formData.workers && formData.workers.length > 0 ? formData.workers.every(w => w.has_valid_certifications) : true
      };

      // Only add permit_number and status for create mode
      if (!isEditMode) {
        payload.permit_number = ''; // Will be auto-generated by backend
        payload.status = 'Draft';
      }

      console.log('Payload to be sent:', payload);

      let response;
      if (isEditMode) {
        // Update existing permit
        response = await axios.put(`${API}/permits/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new permit
        response = await axios.post(`${API}/permits`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setSuccess(true);
      setTimeout(() => {
        if (isEditMode) {
          navigate(`/ptw/${editId}`);
        } else {
          navigate('/ptw');
        }
      }, 2000);
    } catch (err) {
      console.error('Submit error:', err);
      let errorMsg = isEditMode ? 'Failed to update permit' : 'Failed to create permit';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Job Details & Workers', icon: '📋' },
    { number: 2, title: 'Hazards & Controls', icon: '⚠️' },
    { number: 3, title: 'Safety Checklist', icon: '✅' },
    { number: 4, title: 'Special Requirements', icon: '🛡️' }
  ];

  const filteredEmployees = employees.filter(emp => {
    const name = emp.name || '';
    const email = emp.email || '';
    const searchLower = employeeSearch.toLowerCase();
    return name.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower);
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => isEditMode ? navigate(`/ptw/${editId}`) : navigate('/ptw')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isEditMode ? 'Back to Permit' : 'Back to Permits'}
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? `Edit Permit ${permitNumber}` : 'Create Permit to Work'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Update permit details' : 'Issue a new work permit for high-risk activities'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                    currentStep >= step.number
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.number ? <CheckCircle2 className="w-6 h-6" /> : step.icon}
                  </div>
                  <span className={`text-xs mt-2 font-semibold ${
                    currentStep >= step.number ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error/Success Alerts */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">
              {isEditMode ? 'Permit updated successfully! Redirecting...' : 'Permit created successfully! Redirecting...'}
            </AlertDescription>
          </Alert>
        )}

        {/* Initial Loading for Edit Mode */}
        {initialLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading permit data...</span>
          </div>
        )}

        {/* Form Card */}
        {!initialLoading && (
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Job Details & Workers */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Permit Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.permit_type}
                    onChange={(e) => setFormData({ ...formData, permit_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Permit Type</option>
                    {permitTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Work Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.work_description}
                    onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    rows="3"
                    placeholder="Describe the work to be performed"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Work site location"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Applicant Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.applicant_name}
                      onChange={(e) => setFormData({ ...formData, applicant_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Your name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Work Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.work_start_date}
                      onChange={(e) => setFormData({ ...formData, work_start_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Work End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.work_end_date}
                      onChange={(e) => setFormData({ ...formData, work_end_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Contractor Company
                    </label>
                    <input
                      type="text"
                      value={formData.contractor_company}
                      onChange={(e) => setFormData({ ...formData, contractor_company: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="If applicable"
                    />
                  </div>
                </div>

                {/* Worker Competency Validation */}
                {formData.permit_type && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Workers & Competency Validation
                    </h3>
                    
                    {requiredCertifications[formData.permit_type]?.length > 0 && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900 mb-2">Required Certifications:</p>
                        <div className="flex flex-wrap gap-2">
                          {requiredCertifications[formData.permit_type].map(cert => (
                            <span key={cert} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Add Worker
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={employeeSearch}
                            onChange={(e) => setEmployeeSearch(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            placeholder="Search employee by name or email"
                          />
                          {employeeSearch && filteredEmployees.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {filteredEmployees.map(emp => (
                                <div
                                  key={emp.id}
                                  onClick={() => {
                                    setSelectedEmployee(emp);
                                    setEmployeeSearch(emp.name);
                                  }}
                                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                                >
                                  <div className="font-semibold text-gray-900">{emp.name}</div>
                                  <div className="text-xs text-gray-600">{emp.email}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={handleAddWorker}
                          disabled={!selectedEmployee || validatingCompetency}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {validatingCompetency ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Workers List */}
                    {formData.workers.length > 0 && (
                      <div className="space-y-2">
                        {formData.workers.map(worker => (
                          <div
                            key={worker.id}
                            className={`p-4 rounded-lg border-2 ${
                              worker.has_valid_certifications
                                ? 'bg-green-50 border-green-200'
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{worker.employee_name}</span>
                                  {worker.has_valid_certifications ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <AlertTriangle className="w-5 h-5 text-red-600" />
                                  )}
                                </div>
                                {!worker.has_valid_certifications && worker.missing_certifications.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-sm font-semibold text-red-700">Missing Certifications:</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {worker.missing_certifications.map(cert => (
                                        <span key={cert} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                          {cert}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveWorker(worker.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Hazards & Controls */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Hazards & Risk Controls</h3>
                  
                  {/* Link to HIRA */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Link to Existing HIRA (Optional)
                    </label>
                    <select
                      onChange={(e) => linkHIRA(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">Select HIRA record to auto-populate hazards</option>
                      {hiraRecords.map(hira => (
                        <option key={hira.id} value={hira.id}>
                          {hira.title}{hira.location ? ` - ${hira.location}` : ''}
                        </option>
                      ))}
                    </select>
                    {selectedHIRA && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                        ✓ Linked to HIRA: {selectedHIRA.title}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Identified Hazards <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.hazards_identified.join('\n')}
                      onChange={(e) => setFormData({ ...formData, hazards_identified: e.target.value.split('\n') })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      rows="4"
                      placeholder="List each hazard on a new line"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Control Measures <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.control_measures.join('\n')}
                      onChange={(e) => setFormData({ ...formData, control_measures: e.target.value.split('\n') })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      rows="4"
                      placeholder="List each control measure on a new line"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Safety Checklist */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Safety Checklist - {formData.permit_type}</h3>
                
                {formData.safety_checklist.length > 0 ? (
                  <div className="space-y-3">
                    {formData.safety_checklist.map((item, index) => (
                      <div key={item.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={item.checked}
                            onChange={(e) => handleChecklistChange(item.id, 'checked', e.target.checked)}
                            className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <label className="text-sm font-semibold text-gray-900">
                              {index + 1}. {item.question}
                            </label>
                            <input
                              type="text"
                              value={item.remarks || ''}
                              onChange={(e) => handleChecklistChange(item.id, 'remarks', e.target.value)}
                              placeholder="Remarks (optional)"
                              className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Select a permit type in Step 1 to load checklist
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Special Requirements */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Special Requirements</h3>

                {/* Gas Testing */}
                {gasTestRequired && (
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-blue-900">Gas Testing Required</h4>
                      <Button onClick={handleAddGasReading} size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Reading
                      </Button>
                    </div>
                    
                    {formData.gas_test_readings.map((reading, index) => (
                      <div key={reading.id} className="mb-4 p-4 bg-white rounded-lg">
                        <h5 className="font-semibold text-gray-900 mb-3">Reading #{index + 1}</h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">O₂ Level (%)</label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.oxygen_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'oxygen_level', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              placeholder="19.5-23.5%"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">LEL (%)</label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.lel_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'lel_level', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              placeholder="<10%"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">H₂S (ppm)</label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.h2s_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'h2s_level', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              placeholder="<10 ppm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">CO (ppm)</label>
                            <input
                              type="number"
                              step="0.1"
                              value={reading.co_level}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'co_level', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              placeholder="<35 ppm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Tester Name</label>
                            <input
                              type="text"
                              value={reading.tester_name}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'tester_name', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Remarks</label>
                            <input
                              type="text"
                              value={reading.remarks}
                              onChange={(e) => handleUpdateGasReading(reading.id, 'remarks', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Isolation/LOTO */}
                {isolationRequired && (
                  <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-orange-900">Isolation & LOTO Required</h4>
                      <Button onClick={handleAddIsolationPoint} size="sm" className="bg-orange-600 hover:bg-orange-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Point
                      </Button>
                    </div>
                    
                    {formData.isolation_points.map((point, index) => (
                      <div key={point.id} className="mb-4 p-4 bg-white rounded-lg">
                        <h5 className="font-semibold text-gray-900 mb-3">Isolation Point #{index + 1}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Equipment Tag</label>
                            <input
                              type="text"
                              value={point.equipment_tag}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'equipment_tag', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Isolation Type</label>
                            <select
                              value={point.isolation_type}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'isolation_type', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                              <option>Electrical</option>
                              <option>Mechanical</option>
                              <option>Pneumatic</option>
                              <option>Hydraulic</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Lock Number</label>
                            <input
                              type="text"
                              value={point.lock_number}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'lock_number', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Verified By</label>
                            <input
                              type="text"
                              value={point.isolation_verified_by}
                              onChange={(e) => handleUpdateIsolationPoint(point.id, 'isolation_verified_by', e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Rescue Plan (for Confined Space) */}
                {rescuePlanRequired && (
                  <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                    <h4 className="font-bold text-purple-900 mb-3">Rescue Plan</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.rescue_plan_available}
                          onChange={(e) => setFormData({ ...formData, rescue_plan_available: e.target.checked })}
                          className="w-5 h-5 text-purple-600 rounded"
                        />
                        <label className="text-sm font-semibold text-gray-900">
                          Rescue plan and equipment available
                        </label>
                      </div>
                      <textarea
                        value={formData.rescue_plan_description}
                        onChange={(e) => setFormData({ ...formData, rescue_plan_description: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        rows="3"
                        placeholder="Describe rescue procedures and equipment"
                      />
                    </div>
                  </div>
                )}

                {/* Emergency Contacts */}
                <div>
                  <h4 className="font-bold text-gray-900 mb-3">Emergency Contacts <span className="text-red-500">*</span></h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="Emergency contact name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.emergency_contact_number}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_number: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="Emergency contact number"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                variant="outline"
              >
                Previous
              </Button>

              {currentStep < 4 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={
                    (currentStep === 1 && (!formData.permit_type || !formData.work_description || !formData.location)) ||
                    (currentStep === 2 && (formData.hazards_identified.length === 0 || formData.control_measures.length === 0))
                  }
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.emergency_contact_name || !formData.emergency_contact_number}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {isEditMode ? 'Update Permit' : 'Submit Permit'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
};

export default PTWCreate;
